import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import type { ChunkAnalysis } from '../types/diagnostics';
import { formatBytes } from '../utils/statistics';

interface Props {
  analysis: ChunkAnalysis;
}

export const ChunkWaterfall: React.FC<Props> = ({ analysis }) => {
  // Show first 100 chunks max for performance
  const chunks = analysis.chunks.slice(0, 100);

  const waterfallData = chunks.map((c, i) => ({
    index: i,
    duration: Math.round(c.duration * 100) / 100,
    gap: Math.round(c.gap * 100) / 100,
    size: c.size,
  }));

  // Gap histogram
  const gaps = chunks.filter((c) => c.gap > 0).map((c) => c.gap);
  const gapBuckets = buildHistogram(gaps, 10);

  return (
    <div className="bg-dark-900 rounded-xl p-6 border border-dark-700">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">
          Chunk Waterfall
        </h3>
        <div className="flex gap-4 text-xs text-slate-500">
          <span>Chunks: <span className="text-white">{analysis.chunks.length}</span></span>
          <span>Avg size: <span className="text-white">{formatBytes(analysis.avgSize)}</span></span>
          <span>Total: <span className="text-white">{formatBytes(analysis.totalBytes)}</span></span>
        </div>
      </div>

      {/* Waterfall chart */}
      <ResponsiveContainer width="100%" height={180}>
        <BarChart data={waterfallData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
          <XAxis dataKey="index" stroke="#475569" fontSize={10} tickLine={false} />
          <YAxis stroke="#475569" fontSize={10} tickLine={false} label={{ value: 'ms', angle: -90, position: 'insideLeft', fill: '#64748b', fontSize: 10 }} />
          <Tooltip
            contentStyle={{
              background: '#1e293b',
              border: '1px solid #334155',
              borderRadius: '8px',
              fontSize: '11px',
            }}
            formatter={(v: number, name: string) => [
              `${v.toFixed(1)} ms`,
              name === 'duration' ? 'Transfer' : 'Gap',
            ]}
            labelFormatter={(i) => `Chunk #${i} (${formatBytes(waterfallData[i as number]?.size ?? 0)})`}
          />
          <Bar dataKey="gap" stackId="a" fill="#ef4444" fillOpacity={0.6} radius={0} />
          <Bar dataKey="duration" stackId="a" fill="#3b82f6" fillOpacity={0.8} radius={[2, 2, 0, 0]}>
            {waterfallData.map((entry, index) => (
              <Cell
                key={index}
                fill={entry.gap > 50 ? '#ef4444' : entry.gap > 20 ? '#f59e0b' : '#3b82f6'}
                fillOpacity={0.8}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {/* Gap histogram */}
      <h4 className="text-xs text-slate-500 uppercase mt-4 mb-2">Packet Gap Distribution</h4>
      <ResponsiveContainer width="100%" height={120}>
        <BarChart data={gapBuckets} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
          <XAxis dataKey="label" stroke="#475569" fontSize={9} tickLine={false} />
          <YAxis stroke="#475569" fontSize={9} tickLine={false} />
          <Tooltip
            contentStyle={{
              background: '#1e293b',
              border: '1px solid #334155',
              borderRadius: '8px',
              fontSize: '11px',
            }}
          />
          <Bar dataKey="count" fill="#8b5cf6" radius={[3, 3, 0, 0]}>
            {gapBuckets.map((entry, index) => (
              <Cell
                key={index}
                fill={entry.binEnd > 50 ? '#ef4444' : entry.binEnd > 20 ? '#f59e0b' : '#8b5cf6'}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {/* Summary stats */}
      <div className="mt-3 grid grid-cols-3 gap-2 text-center text-xs">
        <div className="bg-dark-850 rounded-lg py-2">
          <div className="text-slate-500">Avg Gap</div>
          <div className="text-white font-semibold">{analysis.avgGap.toFixed(1)} ms</div>
        </div>
        <div className="bg-dark-850 rounded-lg py-2">
          <div className="text-slate-500">Out of Order</div>
          <div className={`font-semibold ${analysis.outOfOrderCount > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
            {analysis.outOfOrderCount}
          </div>
        </div>
        <div className="bg-dark-850 rounded-lg py-2">
          <div className="text-slate-500">Buffer Underruns</div>
          <div className={`font-semibold ${analysis.bufferUnderruns > 0 ? 'text-yellow-400' : 'text-emerald-400'}`}>
            {analysis.bufferUnderruns}
          </div>
        </div>
      </div>
    </div>
  );
};

function buildHistogram(values: number[], bucketCount: number): { label: string; count: number; binStart: number; binEnd: number }[] {
  if (values.length === 0) return [];

  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const bucketSize = range / bucketCount;

  const buckets: { label: string; count: number; binStart: number; binEnd: number }[] = [];

  for (let i = 0; i < bucketCount; i++) {
    const binStart = min + i * bucketSize;
    const binEnd = binStart + bucketSize;
    buckets.push({
      label: `${binStart.toFixed(0)}-${binEnd.toFixed(0)}`,
      count: 0,
      binStart,
      binEnd,
    });
  }

  for (const v of values) {
    const idx = Math.min(Math.floor((v - min) / bucketSize), bucketCount - 1);
    buckets[idx].count++;
  }

  return buckets;
}
